/* Trade Approval Card — Phase 3 human gate.
   ────────────────────────────────────────────────────────────────────────────
   Renders a fully-formed, risk-approved options proposal from
   /api/ai/propose-trade and lets the user Approve or Reject it. The contracts
   input recalculates the TOTAL MAX LOSS live, and a countdown enforces the
   backend's 10-minute proposal expiry. Approve re-checks risk server-side
   (the kill switch / daily loss may have moved) before recording the tap —
   nothing is ever sent to a broker from here. */
import { useState, useEffect, useMemo } from "react";
import { C } from "./_shared";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function apiJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* noop */ }
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  const txt = await res.text();
  return txt ? JSON.parse(txt) : null;
}

// Local design tokens — mirror AIAnalysis.jsx so the card sits flush with
// the verdict/levels cards above it.
const T = {
  card:       { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" },
  sectionLab: { fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em" },
};

const VERDICT_DOT = {
  "STRONG BUY":"#16a34a", "BUY":"#16a34a", "NEUTRAL":"#64748b",
  "SELL":"#dc2626", "STRONG SELL":"#dc2626",
};

function fmtMMSS(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function Row({ label, value, valueColor, mono = true }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid #f1f5f9" }}>
      <span style={{ fontSize:11, color:"#64748b" }}>{label}</span>
      <span style={{ fontSize:13, fontFamily: mono ? "monospace" : "inherit", fontWeight:700, color: valueColor || "#0f172a" }}>{value ?? "—"}</span>
    </div>
  );
}

export default function TradeApprovalCard({ proposal, onResolved }) {
  const [contracts, setContracts] = useState(proposal?.contracts || 1);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [blocks, setBlocks] = useState([]);      // populated if approve re-check fails
  const [resolved, setResolved] = useState(null); // "approved" | "rejected"

  // 1s ticking clock for the countdown.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const expMs = useMemo(() => {
    const d = new Date(proposal?.expires_at);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }, [proposal?.expires_at]);
  const secsLeft = Math.max(0, Math.floor((expMs - nowMs) / 1000));
  const expired = !resolved && secsLeft <= 0;

  const perContract  = +proposal?.max_loss_per_contract || 0;
  const qty          = Math.max(1, parseInt(contracts, 10) || 1);
  const totalMaxLoss = perContract * qty;

  const levels  = proposal?.stock_levels || {};
  const options = proposal?.options || {};
  const warnings = proposal?.risk?.warnings || [];
  const verdict = (proposal?.verdict || "NEUTRAL").toUpperCase();
  const dot = VERDICT_DOT[verdict] || "#64748b";

  const approve = async () => {
    setBusy(true); setError(""); setBlocks([]);
    try {
      const r = await apiJSON("/api/ai/approve-trade", {
        method: "POST",
        body: JSON.stringify({ id: proposal.id, contracts: qty }),
      });
      if (r && r.approved === false) {
        setBlocks(r.blocks || ["Risk engine blocked the trade on final check."]);
        return;
      }
      setResolved("approved");
      if (typeof onResolved === "function") onResolved("approved", proposal.id);
    } catch (e) {
      setError(e.message || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true); setError(""); setBlocks([]);
    try {
      await apiJSON("/api/ai/reject-trade", {
        method: "POST",
        body: JSON.stringify({ id: proposal.id }),
      });
      setResolved("rejected");
      if (typeof onResolved === "function") onResolved("rejected", proposal.id);
    } catch (e) {
      setError(e.message || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  // ── Resolved / expired terminal states ──────────────────
  if (resolved === "approved") {
    return (
      <div data-testid="approval-card-approved" style={{ ...T.card, border:`2px solid ${C.green}`, padding:"16px 20px" }}>
        <div style={{ fontSize:14, fontWeight:800, color:C.gt }}>✓ Trade approved</div>
        <div style={{ fontSize:12, color:C.gray, marginTop:4 }}>
          {proposal.ticker} · {options.direction} · {qty} contract{qty > 1 ? "s" : ""} · max loss ${totalMaxLoss.toFixed(0)}.
          Place the order with your broker — nothing was sent automatically.
        </div>
      </div>
    );
  }
  if (resolved === "rejected") {
    return (
      <div data-testid="approval-card-rejected" style={{ ...T.card, border:"1px solid #e2e8f0", padding:"16px 20px" }}>
        <div style={{ fontSize:14, fontWeight:800, color:C.gray }}>✕ Trade rejected</div>
        <div style={{ fontSize:12, color:C.gray, marginTop:4 }}>This proposal has been dismissed.</div>
      </div>
    );
  }

  return (
    <div data-testid="approval-card" style={{ ...T.card, border:`2px solid ${expired ? "#e2e8f0" : "#fca5a5"}`, padding:0, overflow:"hidden" }}>
      {/* Header — ticker · verdict + countdown */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 20px", borderBottom:"1px solid #f1f5f9", background:"#fef2f2" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontSize:15, fontWeight:800, fontFamily:"monospace", color:"#0f172a" }}>{proposal.ticker}</span>
          <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:700, color:dot }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background:dot, display:"inline-block" }} />
            {verdict}
          </span>
          {proposal.confidence != null && (
            <span style={{ fontSize:11, color:C.gray }}>· {proposal.confidence}% confidence</span>
          )}
          {proposal.live_price != null && (
            <span style={{ fontSize:11, color:C.gray, fontFamily:"monospace" }}>· live ${Number(proposal.live_price).toFixed(2)}</span>
          )}
        </div>
        <div data-testid="approval-countdown" style={{
          fontSize:13, fontWeight:800, fontFamily:"monospace",
          color: expired ? C.gray : secsLeft <= 60 ? C.red : C.amber,
        }}>
          {expired ? "EXPIRED" : `⏳ ${fmtMMSS(secsLeft)}`}
        </div>
      </div>

      <div style={{ padding:"16px 20px", display:"flex", flexDirection:"column", gap:14 }}>
        {/* Two-column: trade levels + options play */}
        <div className="approval-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
          <div>
            <div style={{ ...T.sectionLab, marginBottom:6 }}>Trade Levels</div>
            <Row label="Entry"  value={levels.entry}  valueColor="#4f46e5" />
            <Row label="Stop"   value={levels.stop}   valueColor="#dc2626" />
            <Row label="Target" value={levels.target} valueColor="#16a34a" />
            <Row label="R:R"    value={levels.rr} />
          </div>
          <div>
            <div style={{ ...T.sectionLab, marginBottom:6 }}>Options Play</div>
            <Row label="Strategy" value={options.direction} valueColor="#4f46e5" mono={false} />
            <Row label="Strike"   value={options.strike} />
            <Row label="Expiry"   value={options.expiry} mono={false} />
            <Row label="Max risk / contract" value={options.max_risk} valueColor="#dc2626" mono={false} />
          </div>
        </div>

        {options.rationale && (
          <div style={{ fontSize:11, color:"#64748b", fontStyle:"italic", lineHeight:1.5 }}>{options.rationale}</div>
        )}

        {/* Contracts input + TOTAL MAX LOSS */}
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:16, flexWrap:"wrap", paddingTop:6, borderTop:"1px solid #f1f5f9" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={{ fontSize:11, color:C.gray, fontWeight:500 }}>Contracts</label>
            <input
              type="number" inputMode="numeric" min="1" step="1"
              value={contracts}
              onChange={e => setContracts(e.target.value)}
              disabled={expired || busy}
              data-testid="approval-contracts"
              style={{ padding:"8px 10px", borderRadius:7, border:"0.5px solid #d1d5db", fontSize:14, fontWeight:700, width:110, background:"#fff" }}
            />
            <span style={{ fontSize:10, color:C.muted }}>${perContract.toFixed(0)} max loss / contract</span>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ ...T.sectionLab, color:"#dc2626", marginBottom:2 }}>Total Max Loss</div>
            <div data-testid="approval-total-max-loss" style={{ fontSize:34, fontWeight:900, color:C.red, fontFamily:"monospace", lineHeight:1, letterSpacing:"-0.02em" }}>
              −${totalMaxLoss.toFixed(0)}
            </div>
          </div>
        </div>

        {/* Risk-engine warnings (soft) */}
        {warnings.length > 0 && (
          <div data-testid="approval-warnings" style={{ background:C.ab, border:`1px solid ${C.amber}66`, borderRadius:8, padding:"10px 14px" }}>
            {warnings.map((w, i) => (
              <div key={i} style={{ fontSize:11, color:C.at, fontWeight:600 }}>⚠ {w}</div>
            ))}
          </div>
        )}

        {/* Block reasons surfaced by a failed approve re-check */}
        {blocks.length > 0 && (
          <div data-testid="approval-blocks" style={{ background:C.rb, border:`1px solid ${C.red}`, borderRadius:8, padding:"10px 14px" }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.rt, marginBottom:4 }}>🚫 Risk engine blocked approval</div>
            {blocks.map((b, i) => (
              <div key={i} style={{ fontSize:11, color:C.rt }}>• {b}</div>
            ))}
          </div>
        )}

        {error && (
          <div data-testid="approval-error" style={{ fontSize:12, color:C.red, fontWeight:600 }}>⚠ {error}</div>
        )}

        {expired ? (
          <div data-testid="approval-expired" style={{ fontSize:12, color:C.gray, fontStyle:"italic" }}>
            This proposal expired. Re-run the analysis to propose again.
          </div>
        ) : (
          <div style={{ display:"flex", gap:10 }}>
            <button
              onClick={approve} disabled={busy}
              data-testid="approval-approve-btn"
              style={{
                flex:1, padding:"12px 18px", borderRadius:8, border:"none",
                cursor: busy ? "default" : "pointer", fontSize:14, fontWeight:800,
                background:C.green, color:"#fff", opacity: busy ? 0.6 : 1,
                boxShadow:"0 2px 8px rgba(22,163,74,0.3)",
              }}>
              {busy ? "Working…" : "✓ Approve"}
            </button>
            <button
              onClick={reject} disabled={busy}
              data-testid="approval-reject-btn"
              style={{
                flex:1, padding:"12px 18px", borderRadius:8,
                border:`1px solid ${C.red}`, cursor: busy ? "default" : "pointer",
                fontSize:14, fontWeight:800, background:"#fff", color:C.red,
                opacity: busy ? 0.6 : 1,
              }}>
              ✕ Reject
            </button>
          </div>
        )}

        <div style={{ fontSize:10, color:"#92400e", background:"#fffbeb", border:"1px solid #fef08a", borderRadius:6, padding:"6px 12px" }}>
          Approval records your decision only — no order is sent to any broker automatically. Educational only. Not financial advice.
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .approval-2col { grid-template-columns: 1fr !important; gap: 12px !important; }
        }
      `}</style>
    </div>
  );
}
